import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageCircle, Search, Check, CheckCheck, AlertCircle, Clock,
  User, Send, FileText, ExternalLink, Phone, Mail, UserPlus,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { statusLabel } from "@/lib/constants";
import { maskPhone, maskIfPhone } from "@/lib/phone";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import WhatsAppTemplatePicker from "@/components/crm/WhatsAppTemplatePicker";
import WaText from "@/components/crm/WaText";

/* ─── Shared helpers ─── */

// Last 10 digits — the canonical way to compare phone numbers across formats.
function phoneLast10(p) {
  const d = String(p || "").replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : d;
}

function StatusIcon({ status }) {
  if (status === "sent") return <Check className="inline h-3 w-3 text-muted-foreground" />;
  if (status === "delivered") return <CheckCheck className="inline h-3 w-3 text-muted-foreground" />;
  if (status === "read") return <CheckCheck className="inline h-3 w-3 text-blue-500" />;
  if (status === "failed") return <AlertCircle className="inline h-3 w-3 text-red-500" />;
  return <Clock className="inline h-3 w-3 text-muted-foreground" />;
}

function ProviderTag({ provider }) {
  if (!provider) return null;
  return (
    <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{provider}</span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { day: "numeric", month: "short" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" }) + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/* ─── Main page ─── */

export default function WhatsAppInbox() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  // Which connected number to show. "all" = every number the user can see.
  const [numbers, setNumbers] = useState([]);
  const [numberFilter, setNumberFilter] = useState("all");
  const refreshTimer = useRef(null);

  // The connected numbers double as the inbox filter — same source the composer
  // uses for "send from", so the two pickers can never drift apart.
  useEffect(() => {
    api.get("/wa/senders")
      .then(({ data }) => setNumbers(data.senders || []))
      .catch(() => setNumbers([]));
  }, []);

  const loadConvos = useCallback(async () => {
    const params = {};
    if (numberFilter !== "all") {
      const [provider, accountId] = numberFilter.split(":");
      params.provider = provider;
      if (accountId) params.account_id = accountId;
    }
    try {
      const { data } = await api.get("/wa/conversations", { params });
      setConversations(data.conversations || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [numberFilter]);

  useEffect(() => { loadConvos(); }, [loadConvos]);

  // Coalesce refreshes: a burst of inbound messages would otherwise re-run the
  // whole conversation query once per event.
  useLiveEvents((event) => {
    if (event.type === "whatsapp_message" || event.type === "whatsapp_status") {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(loadConvos, 1000);
    }
  });

  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  // Opening a conversation marks it read for this user (clears its unread badge).
  const openConversation = useCallback(async (c) => {
    setSelected(c);
    if (c.unread_count > 0) {
      // Optimistically zero the badge, then persist.
      setConversations((prev) =>
        prev.map((x) => (x.chat_phone === c.chat_phone ? { ...x, unread_count: 0 } : x)));
      try {
        await api.post(`/wa/conversations/${encodeURIComponent(c.chat_phone)}/read`);
      } catch {
        /* best-effort; loadConvos will reconcile */
      }
    }
  }, []);

  const filtered = conversations.filter((c) =>
    (c.chat_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.chat_phone || "").includes(search)
  );

  return (
    <div className="flex h-[calc(100vh-8rem)] rounded-xl border border-navy/10 bg-white shadow-sm overflow-hidden">
      {/* ─── Left: conversation list ─── */}
      <div className="w-80 shrink-0 flex flex-col border-r border-navy/10">
        <div className="p-3 border-b border-navy/5 space-y-2">
          {/* Number picker — only when more than one number is connected. */}
          {numbers.length > 1 && (
            <Select
              value={numberFilter}
              onValueChange={(v) => { setNumberFilter(v); setSelected(null); setLoading(true); }}
            >
              <SelectTrigger className="h-9 text-sm" data-testid="wa-number-filter">
                <SelectValue placeholder="All numbers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All numbers</SelectItem>
                {numbers.map((s) => (
                  <SelectItem key={`${s.provider}:${s.account_id}`} value={`${s.provider}:${s.account_id || ""}`}>
                    {s.label}{s.number ? ` (${s.number})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 px-4 text-center">
              <MessageCircle className="mx-auto h-8 w-8 text-muted-foreground/30 mb-2" />
              <p className="text-xs text-muted-foreground">
                {search
                  ? "No results."
                  : numberFilter !== "all"
                    ? "No conversations on this number yet."
                    : "No conversations yet."}
              </p>
            </div>
          ) : (
            filtered.map((c) => {
              const isActive = selected?.chat_phone === c.chat_phone;
              return (
                <button
                  key={c.chat_phone}
                  onClick={() => openConversation(c)}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors border-b border-navy/5 last:border-b-0 ${
                    isActive ? "bg-green-50" : "hover:bg-cream/40"
                  }`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 font-semibold text-sm">
                    {c.chat_name ? c.chat_name.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-navy truncate">
                        {maskIfPhone(c.chat_name) || maskPhone(c.chat_phone)}
                      </p>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                        {timeAgo(c.last_timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <p className="text-xs text-muted-foreground truncate">
                        {c.last_direction === "out" ? "You: " : ""}
                        {c.last_text || "[message]"}
                      </p>
                    </div>
                    {(c.providers || []).length > 0 && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {c.providers.map((p) => <ProviderTag key={p} provider={p} />)}
                      </div>
                    )}
                  </div>
                  {c.unread_count > 0 && (
                    <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-600 px-1.5 text-[10px] font-bold text-white shrink-0">
                      {c.unread_count}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* ─── Middle: chat panel ─── */}
      <div className="flex-1 flex flex-col min-w-0">
        {selected ? (
          <ChatPanel
            key={selected.chat_phone}
            phone={selected.chat_phone}
            leadId={selected.lead_id}
            leadName={selected.chat_name}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
            <MessageCircle className="h-16 w-16 text-muted-foreground/20 mb-4" />
            <p className="text-lg font-medium text-navy/40">Select a conversation</p>
            <p className="text-sm mt-1">Choose from the list to view messages</p>
          </div>
        )}
      </div>

      {/* ─── Right: lead / call info panel ─── */}
      {selected && (
        <div className="w-80 shrink-0 border-l border-navy/10 overflow-y-auto bg-cream/10">
          <LeadInfoPanel
            key={selected.chat_phone}
            leadId={selected.lead_id}
            phone={selected.chat_phone}
            onLeadCreated={(lead) => {
              setSelected((s) => ({ ...s, lead_id: lead.id, chat_name: lead.name || s.chat_phone }));
              loadConvos();
            }}
          />
        </div>
      )}
    </div>
  );
}

/* ─── Chat panel (right side) ─── */

function ChatPanel({ phone, leadId, leadName }) {
  const [messages, setMessages] = useState([]);
  const [lastInboundAt, setLastInboundAt] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [senders, setSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState("");
  const bottomRef = useRef(null);
  const navigate = useNavigate();

  const loadMessages = useCallback(async () => {
    if (!phone) return;
    try {
      const { data } = await api.get(`/wa/conversations/${phone}/messages`);
      setMessages(data.messages || []);
      setLastInboundAt(data.last_inbound_at || null);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Load the numbers this user can send from across both providers. The backend
  // suggests the sender in priority order — the number this contact last messaged,
  // then the number linked to their branch, then the default number — so it is
  // used first. Only when the contact's last message came in on the OTHER provider
  // (Meta vs Jalpi) do we switch to that provider instead.
  useEffect(() => {
    api.get("/wa/senders", {
      params: { ...(leadId ? { lead_id: leadId } : {}), ...(phone ? { phone } : {}) },
    })
      .then(({ data }) => {
        const list = data.senders || [];
        setSenders(list);
        const lastInboundProvider = [...(messages || [])]
          .reverse()
          .find((m) => m.direction === "in")?.provider;
        const byInbound = lastInboundProvider
          ? list.find((s) => s.provider === lastInboundProvider)
          : null;
        const byDefault = data.default
          ? list.find((s) => s.provider === data.default.provider && s.account_id === data.default.account_id)
          : null;
        const defaultMatchesInbound = byDefault && (!lastInboundProvider
          || byDefault.provider === lastInboundProvider);
        const chosen = (defaultMatchesInbound ? byDefault : byInbound) || byDefault || list[0] || null;
        setSelectedSenderId(chosen ? `${chosen.provider}:${chosen.account_id}` : "");
      })
      .catch(() => { setSenders([]); setSelectedSenderId(""); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, leadId]);

  useLiveEvents((event) => {
    if (event.type === "whatsapp_message" || event.type === "whatsapp_status") {
      const mine = (leadId && event.lead_id === leadId) ||
        (event.phone && phone && phoneLast10(event.phone) === phoneLast10(phone));
      if (mine) {
        loadMessages();
        // A new inbound landed in the OPEN conversation → keep it read (no badge).
        if (event.type === "whatsapp_message" && phone) {
          api.post(`/wa/conversations/${encodeURIComponent(phone)}/read`).catch(() => {});
        }
      }
    }
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const inWindow = lastInboundAt && (Date.now() - new Date(lastInboundAt).getTime()) < 24 * 60 * 60 * 1000;
  const sel = senders.find((s) => `${s.provider}:${s.account_id}` === selectedSenderId) || null;
  const noSender = senders.length === 0;

  const sendText = async () => {
    const trimmed = text.trim();
    if (!trimmed || !sel) return;
    setSending(true);
    try {
      await api.post("/wa/send", {
        provider: sel.provider,
        account_id: sel.account_id,
        phone,
        text: trimmed,
      });
      setText("");
      await loadMessages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleTemplateSent = () => {
    setTemplateOpen(false);
    loadMessages();
  };

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-navy/10 bg-cream/20">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 font-semibold text-sm">
          {leadName ? leadName.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-navy truncate">{maskIfPhone(leadName) || maskPhone(phone)}</p>
          <p className="text-xs text-muted-foreground">{maskPhone(phone)}</p>
        </div>
        {leadId && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={() => navigate(`/leads/${leadId}`)} className="shrink-0">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open Lead</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#f0ebe3]/30">
        {loading ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            No messages yet. Send a template to start.
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[70%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                m.direction === "out"
                  ? "bg-green-100 text-green-900"
                  : "bg-white text-navy border border-navy/10"
              }`}>
                {m.msg_type === "template" && (
                  <div className="italic text-xs text-muted-foreground mb-1">Template</div>
                )}
                <WaText text={m.body} />
                <div className="flex items-center justify-end gap-1.5 mt-1">
                  <ProviderTag provider={m.provider} />
                  <span className="text-[10px] text-muted-foreground">{formatTime(m.timestamp)}</span>
                  {m.direction === "out" && <StatusIcon status={m.status} />}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-navy/10 bg-white space-y-2">
        {/* Sender picker — only when the user can send from multiple numbers */}
        {senders.length > 1 && (
          <Select value={selectedSenderId} onValueChange={setSelectedSenderId}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Send from…" /></SelectTrigger>
            <SelectContent>
              {senders.map((s) => (
                <SelectItem key={`${s.provider}:${s.account_id}`} value={`${s.provider}:${s.account_id}`}>
                  {s.label}{s.number ? ` (${s.number})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex gap-2 items-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={() => setTemplateOpen(true)} disabled={noSender} className="shrink-0">
                  <FileText className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Send Template</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1">
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
                    placeholder={noSender ? "No WhatsApp number configured" : (inWindow ? "Type a message..." : "Send a template to start a conversation")}
                    disabled={noSender || !inWindow || sending}
                    className="h-10"
                  />
                </div>
              </TooltipTrigger>
              {(noSender || !inWindow) && (
                <TooltipContent>
                  {noSender
                    ? "No WhatsApp number configured"
                    : "Freeform text is only allowed within 24h of the customer's last message. Use a template."}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <Button
            onClick={sendText}
            disabled={noSender || !inWindow || !text.trim() || sending}
            className="bg-green-600 text-white hover:bg-green-700 shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <WhatsAppTemplatePicker
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        leadId={leadId}
        phone={phone}
        sender={sel}
        onSent={handleTemplateSent}
      />
    </>
  );
}

/* ─── Lead / call info panel (far right) ─── */

function PanelRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {Icon && <Icon className="h-4 w-4 shrink-0 text-white/70" />}
      <span className="font-medium text-white">{value || "-"}</span>
      {label && <span className="text-xs text-white/50">{label}</span>}
    </div>
  );
}

function LeadInfoPanel({ leadId, phone, onLeadCreated }) {
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [lastCall, setLastCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [telecallers, setTelecallers] = useState([]);
  const [assignTo, setAssignTo] = useState("");

  const load = useCallback(async () => {
    if (!leadId) { setLead(null); setLastCall(null); setLoading(false); return; }
    setLoading(true);
    try {
      const [leadRes, callsRes] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/leads/${leadId}/calls`).catch(() => ({ data: [] })),
      ]);
      setLead(leadRes.data);
      const calls = callsRes.data || [];
      setLastCall(calls.length ? calls[0] : null);  // calls sorted created_at desc
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const createLead = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${phone}/create-lead`);
      toast.success(data._repeat ? "Linked to existing lead" : "Lead created");
      onLeadCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const openAssign = async () => {
    setAssignOpen(true);
    if (telecallers.length === 0) {
      try {
        const { data } = await api.get("/users", { params: { role: "telecaller" } });
        setTelecallers(data);
      } catch { /* silent */ }
    }
  };

  const submitAssign = async () => {
    if (!assignTo) return;
    setAssigning(true);
    try {
      await api.post("/leads/assign", { lead_ids: [leadId], method: "manual", assigned_to: assignTo });
      toast.success("Lead assigned");
      setAssignOpen(false);
      setAssignTo("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  // ─── No lead yet → Create Lead CTA ───
  if (!leadId || !lead) {
    return (
      <div className="p-4">
        <div className="rounded-xl bg-navy p-5 text-white shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15">
              <User className="h-6 w-6 text-white/80" />
            </div>
            <div>
              <p className="text-sm font-semibold">No lead linked</p>
              <p className="text-xs text-white/60">{maskPhone(phone)}</p>
            </div>
          </div>
        </div>
        <Button
          onClick={createLead}
          disabled={creating}
          className="mt-4 w-full bg-gold text-white hover:bg-gold-hover"
          data-testid="wa-create-lead-btn"
        >
          <UserPlus className="mr-1.5 h-4 w-4" /> {creating ? "Creating…" : "Create Lead"}
        </Button>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Creates a lead from this WhatsApp number and links the conversation.
        </p>
      </div>
    );
  }

  // ─── Has lead → info + last call + actions ───
  return (
    <div className="flex h-full flex-col p-4">
      {/* Lead header card */}
      <div className="rounded-xl bg-navy p-5 text-white shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 text-lg font-semibold">
            {lead.name ? lead.name.charAt(0).toUpperCase() : <User className="h-6 w-6 text-white/80" />}
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold">{lead.name || "-"}</p>
            <p className="text-xs text-white/60">{lead.friendly_id}</p>
          </div>
        </div>
        <div className="mt-4 space-y-2 border-t border-white/10 pt-3">
          <PanelRow icon={Phone} value={maskPhone(lead.mobile || phone)} />
          <PanelRow icon={Mail} value={lead.email} />
          <div className="flex items-center justify-between pt-1 text-sm">
            <span className="text-xs text-white/50">Deal Value</span>
            <span className="font-medium text-white">
              {lead.deal_value != null ? `₹${Number(lead.deal_value).toLocaleString("en-IN")}` : "-"}
            </span>
          </div>
        </div>
      </div>

      {/* Last call details */}
      <div className="mt-4 rounded-xl border border-navy/10 bg-white p-4 shadow-sm">
        <p className="mb-3 text-sm font-semibold text-navy">Last Call Details</p>
        {lastCall ? (
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Caller Name</p>
              <p className="font-medium text-ink">{lastCall.user_name || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="font-medium text-ink">{lastCall.notes || "-"}</p>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <p className="font-medium text-red-600">{statusLabel(lastCall.disposition) || "-"}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Created At</p>
                <p className="font-medium text-ink">{formatTime(lastCall.created_at)}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No calls logged yet.</p>
        )}
      </div>

      {/* Actions */}
      <div className="mt-auto pt-4">
        {assignOpen ? (
          <div className="rounded-lg border border-navy/10 bg-white p-3 shadow-sm">
            <p className="mb-2 text-xs font-semibold text-navy">Assign to tele-caller</p>
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select tele-caller" /></SelectTrigger>
              <SelectContent>
                {telecallers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" className="flex-1" onClick={() => { setAssignOpen(false); setAssignTo(""); }}>Cancel</Button>
              <Button size="sm" className="flex-1 bg-navy text-white hover:bg-navy-hover" disabled={!assignTo || assigning} onClick={submitAssign}>
                {assigning ? "…" : "Confirm"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="border-navy/20 text-navy hover:bg-navy/5" onClick={() => navigate(`/leads/${leadId}`)}>
              Go to Lead
            </Button>
            <Button className="bg-navy text-white hover:bg-navy-hover" onClick={openAssign}>
              Assign Lead
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
